import ErrorComponent from 'Components/ErrorComponent';
import Loading from 'Components/Loading';
import { useFormik } from 'formik';
import env from 'environment_system/env_system';
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query';
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Form, Input, Label, Row } from 'reactstrap';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { checkValidArray, handleServerResponse, saveEditForm, useSafeFormikContext } from 'GenericForms/Helper';
import apiGlobal from 'global/api.global';
import { CountersAndFMPrecedence, EventPrecedence } from 'shared/constants';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import * as Yup from 'yup';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import ErrorTooltip from 'Components/ErrorTooltip';
import { RootState } from 'index';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';

interface CountersFlowmetersComponentType {
    toggleAddTab: (value: number) => void,
    toggleDynamicTabs: (value: number) => void,
    setErrorMessage: any
    editReportBool: boolean
}

const CountersFlowmetersComponent = ({
    toggleAddTab,
    toggleDynamicTabs,
    setErrorMessage,
    editReportBool
}: CountersFlowmetersComponentType) => {
    /** State variables start */
    const { previousEnggTabIndex } = useEditReport();
    const { VesselID, ReportID, VoyageID, CurrentReport, loading } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const fmCountersCurrentReport = CurrentReport?.data?.chief_engineer?.econship_counter_and_flowmeter ?? null;
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const [refreshKey, setRefreshKey] = useState(0);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    /** State variables end */

    /** useQueries */
    /** Load vessel's flowmeters and counters */
    const {
        data: fmCounters,
        isLoading: fmCountersLoading,
        isError: fmCountersError,
    } = useQuery(
        [queryKeyes.vessel.EconFMCounterMaster.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.EconFMCounterMaster.url(VesselID), VesselID);
        },
        { staleTime: Infinity }
    );
    /** useQueries end */

    /** Assign values to CountersFlowmeters's initial object */
    const getInitialValues = () => {
        let arr: any[] = [];
        if (editValues?.chief_engineer) {
            return editValues?.chief_engineer?.econship_counter_and_flowmeter;
        }
        if (checkValidArray(fmCountersCurrentReport)) {
            return fmCountersCurrentReport;
        } else {
            if (fmCounters && fmCounters?.length > 0) {
                fmCounters?.forEach((fm: any) => {
                    arr.push({
                        counter: fm.id,
                        fm_counter_name: fm.fm_counter_name,
                        counter_value: 0,
                        unit: fm.unit,
                        counter_precedence_id: fm.precedence_id,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID
                    })
                })
            }
            return arr;
        }
    }

    /** POST request for Counters & Flowmeters */
    const CountersFlowmetersSubmit = async (values: any, actions: any) => {
        const responseArray: any[] = [];
        if (checkValidArray(fmCountersCurrentReport)) {
            values?.fmCounters?.forEach((fmcounter: any) => {
                responseArray.push(apiGlobal.put(`${queryKeyes.vessel.EconFMCounter.url()}${fmcounter?.id}/`, fmcounter))
            });
        } else {
            responseArray.push(apiGlobal.post(queryKeyes.vessel.EconFMCounter.url(), values?.fmCounters));
        }
        /** handle server response */
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                dispatch(getCurrentReportAction(VesselID, ReportID));
                if (env?.form_validation === true) {
                    setErrorMessage(null)
                    toggleDynamicTabs(previousEnggTabIndex + 1);
                }
                setRefreshKey(refreshKey + 1);
                actions.setSubmitting(false);
            } else {
                setErrorMessage(res)
            }
        });
    }
    const EconCFMUseFormik: any = useFormik<any>({
        enableReinitialize: true,
        initialValues: {
            fmCounters: getInitialValues()
        },
        validationSchema: env?.form_validation === true ?
            Yup.array(
                Yup.object().shape({
                    fmCounters: Yup.array().of(
                        Yup.object().shape({
                            counter_value: Yup.string().matches(/^\d{0,10}(\.\d{1,4})?$/, `${commonValidationMessages.before10after4}`).nullable(),
                        })
                    )
                })
            )
            : null,
        onSubmit: async (values: any, actions: any) => {
            if (env?.form_validation === false) {
                setErrorMessage(null)
                toggleDynamicTabs(previousEnggTabIndex + 1);
            }
            actions.setSubmitting(true);
            CountersFlowmetersSubmit(values, actions);
        },
    });

    useEffect(() => {
        if (editInitialValues?.chief_engineer) {
            setEditValues({
                ...editValues,
                chief_engineer: {
                    ...editValues?.chief_engineer,
                    econship_counter_and_flowmeter: EconCFMUseFormik?.values?.fmCounters,
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [EconCFMUseFormik?.values])

    return (
        <React.Fragment>
            {(fmCountersLoading || loading) && <Loading message='Loading required data!' />}
            {fmCountersError && <ErrorComponent message='Error loading required data!' />}
            {!(fmCountersLoading || loading) && !fmCountersError &&
                <Form onSubmit={EconCFMUseFormik?.handleSubmit} noValidate autoComplete='off' onReset={EconCFMUseFormik.handleReset}>
                    <Card className='p-0 mb-0 border-0'>
                        <CardHeader className='p-2 border-bottom-0'>
                            <h5 className='mb-0'>Counters & Flowmeters</h5>
                        </CardHeader>
                        <CardBody className='p-0'>
                            {/* Revolution counters */}
                            <Card>
                                <CardHeader className='p-2'>
                                    <h5 className='mb-0'>Revolution counters</h5>
                                </CardHeader>
                                <CardBody className='p-2'>
                                    {EconCFMUseFormik?.values && EconCFMUseFormik?.values?.fmCounters?.filter((value: any) => value?.counter_precedence_id === CountersAndFMPrecedence.REVOLUTION_COUNRER).length > 0 ?
                                        <Row>
                                            <React.Fragment>
                                                {EconCFMUseFormik?.values?.fmCounters
                                                    .filter((value: any) => value?.counter_precedence_id === CountersAndFMPrecedence.REVOLUTION_COUNRER)
                                                    .map((value: any, index: number) => {
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <Col sm={3} className='mb-2'>
                                                                    <Label className="mb-0" htmlFor={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}>{value?.fm_counter_name}</Label>
                                                                    <div className='input-group'>
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            id={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}
                                                                            name={`fmCounters.${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}.counter_value`}
                                                                            className="form-cntrol max-width-9 text-right"
                                                                            onChange={(e: any) => EconCFMUseFormik?.handleChange(e)}
                                                                            onBlur={(e: any) => {
                                                                                EconCFMUseFormik?.handleBlur(e);
                                                                                EconCFMUseFormik?.handleChange(e);
                                                                            }}
                                                                            defaultValue={value?.counter_value}
                                                                        />
                                                                        <div className="input-group-text">{value?.unit}</div>
                                                                    </div>
                                                                    {env?.form_validation === true &&
                                                                        EconCFMUseFormik?.errors && EconCFMUseFormik?.errors?.fmCounters && EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)] &&
                                                                        EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                        EconCFMUseFormik?.touched && EconCFMUseFormik?.touched?.fmCounters && EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)] &&
                                                                        EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                        <ErrorTooltip
                                                                            target={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}
                                                                            message={EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value}
                                                                            open={EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                                EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value}
                                                                        />
                                                                    }
                                                                </Col>
                                                            </React.Fragment>
                                                        )
                                                    }
                                                    )}
                                            </React.Fragment>
                                        </Row> :
                                        <Row>
                                            <Col className='text-center'>
                                                <Label className='mb-3'>No Revolution counters are configured for this vessel</Label>
                                            </Col>
                                        </Row>
                                    }
                                </CardBody>
                            </Card>
                            {/* Energy meters */}
                            <Card>
                                <CardHeader className='p-2'>
                                    <h5 className='mb-0'>Energy meter counter</h5>
                                </CardHeader>
                                <CardBody className='p-2'>
                                    {EconCFMUseFormik?.values && EconCFMUseFormik?.values?.fmCounters?.filter((value: any) => value?.counter_precedence_id === CountersAndFMPrecedence.ENERGY_METER).length > 0 ?
                                        <Row>
                                            <React.Fragment>
                                                {EconCFMUseFormik?.values?.fmCounters
                                                    .filter((value: any) => value?.counter_precedence_id === CountersAndFMPrecedence.ENERGY_METER)
                                                    .map((value: any, index: number) => {
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <Col sm={3} className='mb-2'>
                                                                    <Label className="mb-0" htmlFor={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}>{value?.fm_counter_name}</Label>
                                                                    <div className='input-group'>
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            id={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}
                                                                            name={`fmCounters.${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}.counter_value`}
                                                                            className="form-cntrol max-width-9 text-right"
                                                                            onChange={(e: any) => EconCFMUseFormik?.handleChange(e)}
                                                                            onBlur={(e: any) => {
                                                                                EconCFMUseFormik?.handleBlur(e);
                                                                                EconCFMUseFormik?.handleChange(e);
                                                                            }}
                                                                            defaultValue={value?.counter_value}
                                                                        />
                                                                        <div className="input-group-text">{value?.unit}</div>
                                                                    </div>
                                                                    {env?.form_validation === true &&
                                                                        EconCFMUseFormik?.errors && EconCFMUseFormik?.errors?.fmCounters && EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)] &&
                                                                        EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                        EconCFMUseFormik?.touched && EconCFMUseFormik?.touched?.fmCounters && EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)] &&
                                                                        EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                        <ErrorTooltip
                                                                            target={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}
                                                                            message={EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value}
                                                                            open={EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                                EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value}
                                                                        />
                                                                    }
                                                                </Col>
                                                            </React.Fragment>
                                                        )
                                                    }
                                                    )}
                                            </React.Fragment>
                                        </Row> :
                                        <Row>
                                            <Col className='text-center'>
                                                <Label className='mb-3'>No Energy meters are configured for this vessel</Label>
                                            </Col>
                                        </Row>
                                    }
                                </CardBody>
                            </Card>
                            {/* Flowmeters */}
                            <Card className='mb-0'>
                                <CardHeader className='p-2'>
                                    <h5 className='mb-0'>Flowmeters</h5>
                                </CardHeader>
                                <CardBody className='p-2'>
                                    {EconCFMUseFormik?.values && EconCFMUseFormik?.values?.fmCounters?.filter((value: any) => value?.counter_precedence_id === CountersAndFMPrecedence.FLOW_METERS).length > 0 ?
                                        <Row>
                                            <React.Fragment>
                                                {EconCFMUseFormik?.values?.fmCounters
                                                    .filter((value: any) => value?.counter_precedence_id === CountersAndFMPrecedence.FLOW_METERS)
                                                    .map((value: any, index: number) => {
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <Col sm={3} className='mb-2'>
                                                                    <Label className="mb-0" htmlFor={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}>{value?.fm_counter_name}</Label>
                                                                    <div className='input-group'>
                                                                        <Input
                                                                            onFocus={(e) => e.target.select()}
                                                                            type="text"
                                                                            id={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}
                                                                            name={`fmCounters.${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}.counter_value`}
                                                                            className="form-cntrol max-width-9 text-right"
                                                                            onChange={(e: any) => EconCFMUseFormik?.handleChange(e)}
                                                                            onBlur={(e: any) => {
                                                                                EconCFMUseFormik?.handleBlur(e);
                                                                                EconCFMUseFormik?.handleChange(e);
                                                                            }}
                                                                            defaultValue={value?.counter_value}
                                                                        />
                                                                        <div className="input-group-text">{value?.unit}</div>
                                                                    </div>
                                                                    {env?.form_validation === true &&
                                                                        EconCFMUseFormik?.errors && EconCFMUseFormik?.errors?.fmCounters && EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)] &&
                                                                        EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                        EconCFMUseFormik?.touched && EconCFMUseFormik?.touched?.fmCounters && EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)] &&
                                                                        EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                        <ErrorTooltip
                                                                            target={`counter_${EconCFMUseFormik?.values?.fmCounters.indexOf(value)}`}
                                                                            message={EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value}
                                                                            open={EconCFMUseFormik?.errors?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value &&
                                                                                EconCFMUseFormik?.touched?.fmCounters[EconCFMUseFormik?.values?.fmCounters.indexOf(value)]?.counter_value}
                                                                        />
                                                                    }
                                                                </Col>
                                                            </React.Fragment>
                                                        )
                                                    }
                                                    )}
                                            </React.Fragment>
                                        </Row> :
                                        <Row>
                                            <Col className='text-center'>
                                                <Label className='mb-3'>No Flowmeters are configured for this vessel</Label>
                                            </Col>
                                        </Row>
                                    }
                                </CardBody>
                            </Card>
                        </CardBody>
                        <CardFooter className='p-2 py-3'>
                            <Row className="ele_row1">
                                <div className="d-flex flex-wrap gap-5">
                                    <Button
                                        type={editReportBool ? "button" : "submit"}
                                        color="primary"
                                        className="btn_size_cstm pos-end"
                                        disabled={EconCFMUseFormik?.isSubmitting || editIsSubmitting}
                                        onClick={async () => {
                                            const validationErrors = await EconCFMUseFormik?.validateForm();
                                            Object.keys(EconCFMUseFormik?.values || {}).forEach((key: any) => {
                                                EconCFMUseFormik?.setFieldTouched(key, true);
                                            });
                                            if (editReportBool && Object.keys(validationErrors).length === 0) {
                                                await saveEditForm(editSubmit, toggleDynamicTabs, previousEnggTabIndex);

                                            }
                                        }}
                                    >
                                        {(EconCFMUseFormik.isSubmitting || editIsSubmitting) && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                                        Next <i className="bx bx-chevron-right ms-1" />
                                    </Button>
                                    <Button type="button" color="primary" className="btn_size_cstm" onClick={() => {
                                        EconCFMUseFormik.setErrors({})
                                        EconCFMUseFormik.errors = {};
                                        setErrorMessage(null)
                                        if (currentVesselReport?.event_precedence_id === EventPrecedence.NOON ||
                                            currentVesselReport?.only_noon_or_combine_with_other_event === "combine_with_other_report") {
                                            toggleAddTab(2);
                                        } else {
                                            toggleDynamicTabs(previousEnggTabIndex - 1);
                                        }
                                    }}>
                                        <i className="bx bx-chevron-left me-1" />Previous
                                    </Button>
                                </div>
                            </Row>
                        </CardFooter>
                    </Card>
                    <FormValuesDebug values={[EconCFMUseFormik.values, EconCFMUseFormik.errors, EconCFMUseFormik?.touched]} />
                </Form>
            }
        </React.Fragment>
    )
}

export default CountersFlowmetersComponent